import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router'
import axios from 'axios'
import { Chip, Typography, Box, FormControl, InputLabel, Select, MenuItem, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete, Avatar, CircularProgress, Tooltip, IconButton } from '@mui/material'
import { PageHeader } from '../components/shared/PageHeader'
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { themeColors } from '../app/layout/theme'
import { DataTable, type Column } from '../components/ui/DataTable'
import { ListToolbar, type BulkAction } from '../components/shared/ListToolbar'
import { UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef } from '../components/shared/UnifiedFilterDialog'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'
import { observer } from 'mobx-react-lite'
import { usersAdminApi, type AdminUser } from '../lib/api/usersAdminApi'
import { retentionApi } from '../lib/api/retentionApi'
import { FormInput } from '../components/ui'
import { toast } from 'react-toastify'
import { authStore } from '../lib/stores/authStore'
import { getCustomerTypes, type CustomerType } from '../lib/api/customerTypesApi'
import { usePermission } from '../lib/hooks/usePermission'
import { maskPhoneNumber } from '../utils/phoneUtils'

export interface AiRecencyCustomer {
  outlet_id: number
  customer_name: string
  mobile: string
  email: string
  last_order_date: string | null
  recency_days: number
  customer_type_name?: string | null
  customer_type_id?: number | null
  warehouse_id?: number | null
  warehouse_name?: string | null
}

export interface AiRecencyResponse {
  status_code: number
  message: string
  data: AiRecencyCustomer[]
  count: number
  page: number
  limit: number
}

const RECENCY_CUSTOMER_BASE_URL = import.meta.env.VITE_RECENCY_CUSTOMER_API

// `days` is a genuine server-side filter — the API returns only customers whose
// recency_days >= days (i.e. "has not ordered in `days` days or more"), computed
// dynamically off today's date server-side. No client-side date math needed anymore.
export interface RecencyParams {
  days?: number
  page?: number
  limit?: number
  outlet_id?: number
  fromDate?: string
  toDate?: string
  warehouse_id?: number
  customer_type_id?: number
  mobile?: string
  email?: string
  name?: string
  isConnected?: boolean
}

export async function fetchRecency(params: RecencyParams): Promise<AiRecencyResponse> {
  const res = await axios.get<AiRecencyResponse>(`${RECENCY_CUSTOMER_BASE_URL}/recency`, {
    params,
  })
  return res.data
}

function formatDate(iso: string | null) {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) {
  // let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  // return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
  return themeColors.primary;
}

const SalesCustomerRetentionPage = observer(function SalesCustomerRetentionPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState(() => {
    return sessionStorage.getItem('retention_search') ?? ''
  })
  // selectedRowsMap persists full row data across page changes so bulk assign works correctly
  const [selectedRowsMap, setSelectedRowsMap] = useState<Map<string, any>>(new Map())
  const selected = useMemo(() => new Set(selectedRowsMap.keys()), [selectedRowsMap])
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false)
  const [bulkAssignUser, setBulkAssignUser] = useState<AdminUser | null>(null)
  const qc = useQueryClient()

  const [localDays, setLocalDays] = useState<number | string>(() => {
    const saved = sessionStorage.getItem('retention_days')
    return saved ? Number(saved) : 21
  })
  const [days, setDays] = useState<number>(() => {
    const saved = sessionStorage.getItem('retention_days')
    return saved ? Number(saved) : 21
  })

  useEffect(() => {
    const t = setTimeout(() => {
      setDays(Math.max(0, Number(localDays) || 0))
    }, 400)
    return () => clearTimeout(t)
  }, [localDays])
  const [limit, setLimit] = useState(() => {
    const saved = sessionStorage.getItem('retention_limit')
    return saved ? Number(saved) : 10000
  })
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem('retention_page')
    return saved ? Number(saved) : 1
  })
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem('retention_page_size')
    return saved ? Number(saved) : 10
  })
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(() => {
    const saved = sessionStorage.getItem('retention_sort_order')
    return (saved === 'ASC' || saved === 'DESC') ? saved : 'ASC'
  })
  const [isConnectedFilter, setIsConnectedFilter] = useState<'All' | 'Connected' | 'Not Connected'>(() => {
    const saved = sessionStorage.getItem('retention_connected')
    return (saved === 'All' || saved === 'Connected' || saved === 'Not Connected') ? saved : 'All'
  })

  useEffect(() => {
    sessionStorage.setItem('retention_connected', isConnectedFilter)
  }, [isConnectedFilter])

  const [filters, setFilters] = useState<UnifiedFilters>(() => {
    const saved = sessionStorage.getItem('crm-retention-filters')
    if (saved) {
      try { return JSON.parse(saved) } catch { }
    }
    return EMPTY_UNIFIED_FILTERS
  })

  useEffect(() => {
    sessionStorage.setItem('retention_search', search)
  }, [search])

  useEffect(() => {
    sessionStorage.setItem('retention_days', String(days))
  }, [days])

  useEffect(() => {
    sessionStorage.setItem('retention_limit', String(limit))
  }, [limit])

  useEffect(() => {
    sessionStorage.setItem('retention_page', String(page))
  }, [page])

  useEffect(() => {
    sessionStorage.setItem('retention_page_size', String(pageSize))
  }, [pageSize])

  useEffect(() => {
    sessionStorage.setItem('retention_sort_order', sortOrder)
  }, [sortOrder])

  useEffect(() => {
    sessionStorage.setItem('crm-retention-filters', JSON.stringify(filters))
  }, [filters])

  // Permission checks for retention features
  const { hasAction } = usePermission()
  const canGetAllRecency = hasAction('/customer-retention', 'getAllRecencyCustomers')
  const canAssign = hasAction('/customer-retention', 'assign')

  // Restricted users cannot see all recency customers — they only see their own assigned ones.
  // For them we skip the AI Recency API entirely and query CRM DB directly.
  const isRestrictedUser = !canGetAllRecency
  const currentUserId = authStore.user?.userId ?? null

  const { isSuperAdmin, selectedOrgName } = useOrgSelector()
  const navOrgFilter = useOrgFilter()

  const isSalesUser = !isSuperAdmin && !!authStore.user?.lobId && authStore.user.lobId !== '00000000-0000-0000-0000-000000000000'

  // Fetch master Customer Types from DB
  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types-retention'],
    queryFn: getCustomerTypes,
    staleTime: 5 * 60 * 1000,
  })

  // Fetch master Warehouses from DB (/ware-houses)
  const { data: masterWarehouses = [] } = useQuery({
    queryKey: ['master-warehouses-retention'],
    queryFn: retentionApi.getWarehouses,
    staleTime: 5 * 60 * 1000,
  })

  // Load users list for bulk assignment and filtering
  const { data: usersData } = useQuery({
    queryKey: ['users-for-assign-retention'],
    queryFn: () => usersAdminApi.getAll({ pageSize: 1000 }),
    staleTime: 60_000,
  })
  const usersList = usersData?.items ?? []

  const orgIdForFetching = navOrgFilter.orgId

  // Resolve selected assignee names
  const selectedAssignees = filters.checkboxes.assignedTo ?? []
  const assigneeFilterActive = selectedAssignees.length > 0 && !selectedAssignees.includes('unassigned')
  const includesUnassigned = selectedAssignees.includes('unassigned')

  // Build params for AI Recency API
  const apiParams = useMemo<RecencyParams>(() => {
    const p: RecencyParams = {
      page,
      limit: pageSize,
      days,
      // isConnected: isConnectedFilter === 'Connected' ? true : isConnectedFilter === 'Not Connected' ? false : undefined,
    }

    if (search.trim()) {
      const q = search.trim()
      const qNum = Number(q)
      if (!isNaN(qNum)) {
        p.outlet_id = qNum
      } else {
        p.name = q
      }
    }

    if (filters.checkboxes.customerType?.length) {
      const selectedTypeName = filters.checkboxes.customerType[0]
      const foundType = customerTypes.find(t => t.name === selectedTypeName)
      if (foundType) {
        p.customer_type_id = foundType.id
      }
    }

    if (filters.checkboxes.warehouseId?.length) {
      p.warehouse_id = Number(filters.checkboxes.warehouseId[0])
    }

    if (filters.dateFrom) p.fromDate = filters.dateFrom
    if (filters.dateTo) p.toDate = filters.dateTo

    return p
  }, [page, pageSize, days, search, filters, customerTypes, isConnectedFilter])

  // Fetch AI recency list via server-side params.
  // The query key must change when the connected filter changes so React Query does not reuse stale row sets.
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['sales-ai-recency-customers', apiParams],
    queryFn: () => fetchRecency(apiParams),
    enabled: !assigneeFilterActive && !isRestrictedUser,
    staleTime: 30_000,
  })

  const rows = (assigneeFilterActive || isRestrictedUser) ? [] : (data?.data ?? [])

  // Collect displayed outlet IDs for targeted batch lookup from CRM DB
  const displayedOutletIds = useMemo(() => {
    return rows.map(r => String(r.outlet_id)).filter(Boolean)
  }, [rows])

  const { data: localRetentionData } = useQuery({
    queryKey: ['local-retention-customers-batch', displayedOutletIds],
    queryFn: () => retentionApi.getByOutletIds(displayedOutletIds),
    enabled: displayedOutletIds.length > 0,
    staleTime: 30_000,
  })
  const localRetentionCustomers = localRetentionData ?? []

  // Resolve selected assignee names → user GUIDs from DB users list
  const selectedOwnerIds = useMemo(() => {
    return selectedAssignees
      .filter(a => a !== 'unassigned')
      .map(name => usersList.find(u => u.name === name)?.id)
      .filter((id): id is string => !!id)
  }, [selectedAssignees, usersList])

  // Single indexed paginated query to CRM DB for all selected owner GUIDs (GET /retention-customers/paged)
  const { data: ownerFilteredData, isLoading: ownerFilterLoading } = useQuery({
    queryKey: ['retention-by-owners-paged', selectedOwnerIds, orgIdForFetching, page, pageSize, search, filters, isConnectedFilter],
    queryFn: () => retentionApi.getPagedByOwnerIds({
      orgId: orgIdForFetching,
      ownerIds: selectedOwnerIds,
      page,
      pageSize,
      search: search.trim() || undefined,
      customerType: filters.checkboxes.customerType?.length ? filters.checkboxes.customerType[0] : undefined,
      warehouseId: filters.checkboxes.warehouseId?.length ? Number(filters.checkboxes.warehouseId[0]) : undefined,
      isConnected: isConnectedFilter === 'Connected' ? true : isConnectedFilter === 'Not Connected' ? false : undefined,
    }),
    enabled: assigneeFilterActive && selectedOwnerIds.length > 0,
    staleTime: 30_000,
  })

  // Restricted users: directly load only their own assigned customers from CRM DB (paginated)
  const { data: myAssignedData, isLoading: myAssignedLoading } = useQuery({
    queryKey: ['retention-my-assigned-paged', currentUserId, orgIdForFetching, page, pageSize, search, filters, isConnectedFilter],
    queryFn: () => retentionApi.getPagedByOwnerIds({
      orgId: orgIdForFetching,
      ownerId: currentUserId!,
      page,
      pageSize,
      search: search.trim() || undefined,
      customerType: filters.checkboxes.customerType?.length ? filters.checkboxes.customerType[0] : undefined,
      warehouseId: filters.checkboxes.warehouseId?.length ? Number(filters.checkboxes.warehouseId[0]) : undefined,
      isConnected: isConnectedFilter === 'Connected' ? true : isConnectedFilter === 'Not Connected' ? false : undefined,
    }),
    enabled: isRestrictedUser && !!currentUserId,
    staleTime: 30_000,
  })

  // When assignee filter is ON → use CRM DB rows; otherwise use AI API rows
  const crmAssigneeRows = ownerFilteredData?.items ?? []

  // Enrich AI recency rows with CRM retention DB owner mapping
  const enrichedRows = useMemo(() => {
    return rows.map(row => {
      const matchedRetention = localRetentionCustomers.find(r =>
        r.outletId === String(row.outlet_id)
      )

      return {
        ...row,
        retentionId: matchedRetention?.id ?? null,
        ownerId: matchedRetention?.ownerId ?? null,
        ownerName: matchedRetention?.ownerName ?? null,
        assignedAt: matchedRetention?.assignedAt ?? null,
        isConnected: matchedRetention?.isConnected ?? false,
        orgId: matchedRetention?.orgId ?? null,
        warehouse_id: row.warehouse_id ?? matchedRetention?.warehouseId ?? null,
        warehouse_name: row.warehouse_name ?? matchedRetention?.warehouseName ?? null,
      }
    })
  }, [rows, localRetentionCustomers])

  // Map CRM retention DB rows to the same shape as AI rows (for Assignee filter mode)
  const enrichedCrmRows = useMemo(() => {
    return crmAssigneeRows.map(r => ({
      outlet_id: Number(r.outletId) || 0,
      customer_name: r.customerName,
      mobile: r.mobile ?? null,
      email: r.email ?? null,
      recency_days: r.recencyDays,
      last_order_date: r.lastOrderDate ?? null,
      customer_type_id: r.customerTypeId ?? null,
      customer_type_name: r.customerType ?? null,
      assignedAt: r.assignedAt ?? null,
      isConnected: r.isConnected ?? false,
      warehouse_id: r.warehouseId ?? null,
      warehouse_name: r.warehouseName ?? null,
      retentionId: r.id,
      ownerId: r.ownerId,
      ownerName: r.ownerName,
      orgId: r.orgId,
    }))
  }, [crmAssigneeRows])

  // Map restricted user's own assigned CRM rows to the same display shape
  const myAssignedRows = useMemo(() => {
    if (!isRestrictedUser) return []
    return (myAssignedData?.items ?? []).map(r => ({
      outlet_id: Number(r.outletId) || 0,
      customer_name: r.customerName,
      mobile: r.mobile ?? null,
      email: r.email ?? null,
      recency_days: r.recencyDays,
      last_order_date: r.lastOrderDate ?? null,
      customer_type_id: r.customerTypeId ?? null,
      customer_type_name: r.customerType ?? null,
      assignedAt: r?.assignedAt ?? null,  
      isConnected: r?.isConnected ?? false, 
      warehouse_id: r.warehouseId ?? null,
      warehouse_name: r.warehouseName ?? null,
      retentionId: r.id,
      ownerId: r.ownerId,
      ownerName: r.ownerName,
      orgId: r.orgId,
    }))
  }, [isRestrictedUser, myAssignedData])

  // Set of all outlet IDs that have an assigned owner in CRM DB (for Unassigned detection)
  const allAssignedOutletIds = useMemo(() => {
    const set = new Set<string>()
    localRetentionCustomers.forEach(r => {
      if (r.ownerId && r.ownerName) {
        set.add(r.outletId)
      }
    })
    return set
  }, [localRetentionCustomers])


  // Filter rows by organization and user assignment scope
  const orgFilteredRows = useMemo(() => {
    const currentOrgName = isSuperAdmin ? selectedOrgName : (authStore.user?.orgName ?? '')
    if (currentOrgName !== 'All Organizations' && currentOrgName !== 'Momentum') {
      return []
    }

    // When Assignee filter is active → use CRM DB rows (already server-filtered by ownerId)
    if (assigneeFilterActive) {
      return enrichedCrmRows
    }

    // Restricted users → use their own pre-fetched CRM DB rows (AI API was never called)
    if (isRestrictedUser) {
      return myAssignedRows
    }

    // Full-access users → use enriched AI recency rows
    return enrichedRows
  }, [enrichedRows, enrichedCrmRows, myAssignedRows, assigneeFilterActive, isRestrictedUser, isSuperAdmin, selectedOrgName, authStore.user])

  // Apply search & multi-tenant filters client-side, then sort by recency
  const filteredRows = useMemo(() => {
    let result = orgFilteredRows

    if (search.trim()) {
      const q = search.trim().toLowerCase()
      const qDigits = q.replace(/[^0-9]/g, '')
      result = result.filter(r => {
        const name = r.customer_name?.toLowerCase() ?? ''
        const email = r.email?.toLowerCase() ?? ''
        const mobile = r.mobile ?? ''
        const mobileDigits = mobile.replace(/[^0-9]/g, '')
        const outletId = String(r.outlet_id ?? '')

        return (
          name.includes(q) ||
          email.includes(q) ||
          mobile.toLowerCase().includes(q) ||
          (qDigits.length > 0 && mobileDigits.includes(qDigits)) ||
          outletId.includes(q)
        )
      })
    }

    // Filter by Customer Type (robust ID + case-insensitive Name match)
    if (filters.checkboxes.customerType?.length) {
      const selectedNamesLower = filters.checkboxes.customerType.map(s => s.toLowerCase())
      const selectedTypeIds = customerTypes
        .filter(ct => selectedNamesLower.includes(ct.name.toLowerCase()))
        .map(ct => ct.id)

      result = result.filter(r => {
        if (r.customer_type_id != null && selectedTypeIds.includes(Number(r.customer_type_id))) {
          return true
        }
        if (r.customer_type_name && selectedNamesLower.includes(r.customer_type_name.toLowerCase())) {
          return true
        }
        return false
      })
    }

    // Filter by Warehouse (for CRM DB rows)
    if (filters.checkboxes.warehouseId?.length) {
      const selectedWarehouseIds = filters.checkboxes.warehouseId.map(Number)
      result = result.filter(r =>
        r.warehouse_id != null && selectedWarehouseIds.includes(Number(r.warehouse_id))
      )
    }

    // Assignee filter for named users is handled server-side (retentionApi.getAll with ownerId).
    // Only handle 'Unassigned' client-side here — these are AI rows with no CRM DB owner.
    if (includesUnassigned && !assigneeFilterActive) {
      result = result.filter(r => {
        const outletIdStr = String(r.outlet_id)
        return !r.ownerName || !allAssignedOutletIds.has(outletIdStr)
      })
    }

    result = [...result].sort((a, b) =>
      sortOrder === 'ASC' ? a.recency_days - b.recency_days : b.recency_days - a.recency_days
    )

    return result
  }, [orgFilteredRows, filters, search, sortOrder, allAssignedOutletIds, includesUnassigned, assigneeFilterActive, customerTypes])

  const totalCount = assigneeFilterActive
    ? (ownerFilteredData?.total ?? 0)
    : isRestrictedUser
      ? (myAssignedData?.total ?? 0)
      : (data?.count ?? 0)

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  // Only reset to page 1 if data is fully loaded and page exceeds totalPages
  const activeLoading = assigneeFilterActive ? ownerFilterLoading : isRestrictedUser ? myAssignedLoading : isLoading
  const activeData = assigneeFilterActive ? ownerFilteredData : isRestrictedUser ? myAssignedData : data

  useEffect(() => {
    setPage(1)
  }, [isConnectedFilter, search, filters.checkboxes.assignedTo, filters.checkboxes.customerType, filters.checkboxes.warehouseId, filters.dateFrom, filters.dateTo])

  useEffect(() => {
    if (!activeLoading && activeData && totalPages > 0 && page > totalPages) {
      setPage(1)
    }
  }, [activeLoading, activeData, totalPages, page])

  // All queries (AI Recency API, Assignee DB query, Restricted User DB query) are server-paginated
  const pagedRows = filteredRows

  // selectedCustomers uses Map values so data is preserved even when navigating pages
  const selectedCustomers = useMemo(() => Array.from(selectedRowsMap.values()), [selectedRowsMap])

  const selectedOrgIds = useMemo(() => Array.from(new Set(selectedCustomers.map(c => c.orgId).filter(Boolean))), [selectedCustomers])
  const hasMultipleOrgsSelected = selectedOrgIds.length > 1
  const targetOrgId = selectedOrgIds.length === 1 ? selectedOrgIds[0] : null

  const filteredUsersList = useMemo(() => {
    const activeOrgId = isSuperAdmin ? orgIdForFetching : authStore.user?.orgId
    return usersList.filter(u => {
      if (u.roleName?.toUpperCase() === 'SUPER_ADMIN') return false
      if (activeOrgId && u.orgId !== activeOrgId) return false
      if (targetOrgId && u.orgId !== targetOrgId) return false
      return true
    })
  }, [usersList, targetOrgId, isSuperAdmin, orgIdForFetching, authStore.user?.orgId])

  const bulkAssignMutation = useMutation({
    mutationFn: async () => {
      if (!bulkAssignUser) return
      
      const payload = selectedCustomers.map(row => ({
        outletId: String(row.outlet_id),
        customerName: row.customer_name,
        mobile: row.mobile || null,
        email: row.email || null,
        recencyDays: row.recency_days,
        lastOrderDate: row.last_order_date ? new Date(row.last_order_date).toISOString() : null,
        customerTypeId: row.customer_type_id ?? null,
        customerType: row.customer_type_name ?? null,
        warehouseId: row.warehouse_id ?? null,
        warehouseName: row.warehouse_name ?? null,
      }))

      await retentionApi.bulkAssign(payload, bulkAssignUser.id)
    },
    onSuccess: () => {
      toast.success(`Retention customers successfully assigned to ${bulkAssignUser?.name}`)
      setSelectedRowsMap(new Map())
      setBulkAssignOpen(false)
      setBulkAssignUser(null)
      qc.invalidateQueries({ queryKey: ['sales-ai-recency-customers'] })
      qc.invalidateQueries({ queryKey: ['local-customers-lookup'] })
      qc.invalidateQueries({ queryKey: ['local-retention-customers-lookup'] })
      qc.invalidateQueries({ queryKey: ['retention-by-owner'] })
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to bulk assign owners')
    }
  })

  const bulkUnassignMutation = useMutation({
    mutationFn: async () => {
      const ZERO_GUID = '00000000-0000-0000-0000-000000000000'
      const payload = selectedCustomers.map(row => ({
        outletId: String(row.outlet_id),
        customerName: row.customer_name,
        mobile: row.mobile || null,
        email: row.email || null,
        recencyDays: row.recency_days,
        lastOrderDate: row.last_order_date ? new Date(row.last_order_date).toISOString() : null,
        customerTypeId: row.customer_type_id ?? null,
        customerType: row.customer_type_name ?? null,
        warehouseId: row.warehouse_id ?? null,
        warehouseName: row.warehouse_name ?? null,
      }))

      await retentionApi.bulkAssign(payload, ZERO_GUID)
    },
    onSuccess: () => {
      toast.success(`Retention customers successfully unassigned`)
      setSelectedRowsMap(new Map())
      qc.invalidateQueries({ queryKey: ['sales-ai-recency-customers'] })
      qc.invalidateQueries({ queryKey: ['local-customers-lookup'] })
      qc.invalidateQueries({ queryKey: ['local-retention-customers-lookup'] })
      qc.invalidateQueries({ queryKey: ['retention-by-owner'] })
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to unassign owners')
    }
  })

  const bulkActions = useMemo<BulkAction[]>(() => {
    return [
      {
        label: 'Assign to…',
        onClick: () => setBulkAssignOpen(true),
      },
      {
        label: 'Unassign',
        onClick: () => {
          if (window.confirm(`Are you sure you want to unassign these ${selected.size} customer(s)?`)) {
            bulkUnassignMutation.mutate()
          }
        },
      }
    ]
  }, [selected, selectedCustomers, bulkUnassignMutation])

  // Define Filter Definitions for Toolbar with static tabs
  const filterDefs: FilterDef[] = useMemo(() => {
    const defs: FilterDef[] = []

    // 1. Customer Type filter (single select / radio)
    defs.push({
      type: 'checkbox',
      key: 'customerType',
      label: 'Customer Type',
      singleSelect: true,
      options: customerTypes.map(t => ({ label: t.name, value: t.name })),
    })

    // 2. Warehouse filter (single select / radio)
    defs.push({
      type: 'checkbox',
      key: 'warehouseId',
      label: 'Warehouse',
      singleSelect: true,
      options: masterWarehouses.map(w => ({ label: w.warehouseName, value: String(w.warehouseExternalId) })),
    })

    // 3. Assignee User filter (populated from all DB users) - only available if user has permission to see all recency customers
    if (canGetAllRecency) {
      const dbUserNames = Array.from(new Set(usersList.map(u => u.name).filter(Boolean)))
      const ownerOptions = dbUserNames.map(name => ({ label: name, value: name }))
      ownerOptions.push({ label: 'Unassigned', value: 'unassigned' })

      defs.push({
        type: 'checkbox',
        key: 'assignedTo',
        label: 'Assignee',
        options: ownerOptions,
      })
    }

    return defs
  }, [customerTypes, masterWarehouses, usersList, canGetAllRecency])

  const columns: Column[] = [
    {
      id: 'customer_name',
      label: 'Customer',
      sortable: true,
      render: (_, row: AiRecencyCustomer) => (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
          {row.customer_name || '-'}
        </Typography>
      ),
    },
    { id: 'mobile', label: 'Mobile', sortable: true, render: (_, row: AiRecencyCustomer) => maskPhoneNumber(row.mobile) },
    { id: 'last_order_date', label: 'Last Order', sortable: true, render: (_, row: AiRecencyCustomer) => formatDate(row.last_order_date) },
    {
      id: 'owner_name',
      label: 'Assigned To',
      sortable: true,
      render: (_, row: any) => {
        if (!row.ownerName) {
          return (
            <Chip
              label="Unassigned"
              size="small"
              sx={{
                height: 20,
                borderRadius: '4px',
                bgcolor: '#F3F4F6',
                color: '#6B7280',
                fontSize: '11px',
                fontWeight: 500,
              }}
            />
          )
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={{
                width: 20,
                height: 20,
                fontSize: '9px',
                bgcolor: avatarColor(row.ownerName),
              }}
            >
              {row.ownerName.split(' ').slice(0, 2).map((n: string) => n[0]?.toUpperCase() ?? '').join('')}
            </Avatar>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: themeColors.text.primary }}>
              {row.ownerName}
            </Typography>
          </Box>
        )
      }
    },
    // 1. Assigned Date Column
    {
      id: 'assignedAt',
      label: 'Assigned Date',
      minWidth: 130,
      sortable: true,
      render: (_, row: any) => (
        <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, fontWeight: 500 }}>
          {formatDate(row.assignedAt)}
        </Typography>
      ),
    },
    // 2. Connected Status Column
    {
      id: 'isConnected',
      label: 'Agent Connected',
      minWidth: 140,
      sortable: true,
      render: (_, row: any) => {
        const connected = Boolean(row.isConnected)
        return (
          <Chip
            label={connected ? 'Connected' : 'Not Connected'}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: connected ? '#e8f5e9' : '#f5f5f5',
              color: connected ? '#2e7d32' : '#757575',
              border: `1px solid ${connected ? '#a5d6a7' : '#e0e0e0'}`,
            }}
          />
        )
      },
    },
    {
      id: 'customer_type',
      label: 'Customer Type',
      sortable: true,
      render: (_, row: AiRecencyCustomer) => (
        <Chip
          label={row.customer_type_name || 'General'}
          size="small"
          sx={{
            height: 20,
            borderRadius: '4px',
            bgcolor: '#eff6ff',
            color: '#1d4ed8',
            fontSize: '11px',
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      id: 'warehouse_name',
      label: 'Warehouse',
      sortable: true,
      render: (_, row: any) => {
        const whName = row.warehouse_name || masterWarehouses.find(w => w.warehouseExternalId === row.warehouse_id)?.warehouseName
        return (
          <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, fontWeight: 500 }}>
            {whName || '-'}
          </Typography>
        )
      },
    },
    {
      id: 'recency_days',
      label: 'Recency',
      align: 'right',
      sortable: true,
      render: (_, row: AiRecencyCustomer) => (
        <Chip
          label={`${row.recency_days} days`}
          size="small"
          sx={{
            height: 20,
            borderRadius: '4px',
            bgcolor: '#FEF3F2',
            color: '#B42318',
            fontSize: '11px',
            fontWeight: 700,
          }}
        />
      ),
    },
  ]

  const recencyControls = (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <TextField
        size="small"
        type="number"
        label="Recency Days"
        value={localDays}
        onChange={(e) => setLocalDays(e.target.value)}
        inputProps={{ min: 0 }}
        sx={{ width: 100, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '38px' } }}
      />
    </Box>
  )

  const isAssigneeActive = canGetAllRecency ? assigneeFilterActive : true;
  const connectedToggle = isAssigneeActive ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        bgcolor: '#f8fafc',
        p: 0.5,
        gap: 0.5,
        height: '40px',
      }}
    >
      <Button
        size="small"
        onClick={() => setIsConnectedFilter('Connected')}
        sx={{
          borderRadius: '6px',
          textTransform: 'none',
          fontSize: '13px',
          fontWeight: 600,
          px: 1.5,
          height: '100%',
          border: '1px solid transparent',
          ...(isConnectedFilter === 'Connected'
            ? {
              background: '#fff',
              color: themeColors.primary,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              borderColor: '#e2e8f0',
              '&:hover': { background: '#fff' },
            }
            : {
              color: '#64748b',
              bgcolor: 'transparent',
              '&:hover': { borderColor: themeColors.primaryDark, bgcolor: 'transparent' },
            }),
        }}
      >
        Connected
      </Button>
      <Button
        size="small"
        onClick={() => setIsConnectedFilter('Not Connected')}
        sx={{
          borderRadius: '6px',
          textTransform: 'none',
          fontSize: '13px',
          fontWeight: 600,
          px: 1.5,
          height: '100%',
          border: '1px solid transparent',
          ...(isConnectedFilter === 'Not Connected'
            ? {
              background: '#fff',
              color: themeColors.primary,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              borderColor: '#e2e8f0',
              '&:hover': { background: '#fff' },
            }
            : {
              color: '#64748b',
              bgcolor: 'transparent',
              '&:hover': { borderColor: themeColors.primaryDark, bgcolor: 'transparent' },
            }),
        }}
      >
        Not Connected
      </Button>
      {isConnectedFilter !== 'All' && (
        <Tooltip title="Clear selection">
          <IconButton
            size="small"
            onClick={() => setIsConnectedFilter('All')}
            sx={{
              ml: 0.5,
              width: 24,
              height: 24,
              color: '#64748b',
              '&:hover': { color: themeColors.error, bgcolor: '#fef2f2' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  ) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Customer Retention"
        breadcrumb="CRM"
      />

      {/* ── Pinned toolbar ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
        <ListToolbar
          search={search}
          onSearch={setSearch}
          searchPlaceholder="Search retention customers..."
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={setFilters}
          leftExtra={connectedToggle}
          extraActions={recencyControls}
          selectedCount={selected.size}
          onClearSelection={() => setSelectedRowsMap(new Map())}
          bulkActions={!canAssign ? [] : bulkActions}
        />
      </Box>

      {/* ── Scrollable content ── */}
      <Box sx={{ flex: 1, px: 3, pb: 3, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <DataTable
            columns={columns}
            rows={pagedRows}
            isLoading={isLoading}
            isFetching={isFetching || (assigneeFilterActive && ownerFilterLoading)}
            selectable={canAssign}
            selectedIds={selected}
            onSelectionChange={(newSet: Set<string>) => {
              setSelectedRowsMap(prev => {
                const next = new Map(prev)
                // Add newly selected rows (with full row data)
                pagedRows.forEach((row: any) => {
                  const key = String(row.outlet_id)
                  if (newSet.has(key)) {
                    next.set(key, row)
                  } else {
                    next.delete(key)
                  }
                })
                // Keep rows from other pages that are still selected
                prev.forEach((_row, key) => {
                  if (!pagedRows.some((r: any) => String(r.outlet_id) === key)) {
                    if (!newSet.has(key)) {
                      next.delete(key)
                    }
                  }
                })
                return next
              })
            }}
            // showRowNumbers
            rowKey={(row: AiRecencyCustomer) => String(row.outlet_id)}
            onRowClick={(row) => navigate(`/customer-retention/${row.outlet_id}`, { state: { customer: row } })}
            emptyIcon={<SearchIcon />}
            emptyMessage="No contacts found"
            emptyHint="The AI recency API did not return contacts matching your filters"
            pagination={{
              page,
              pageSize,
              total: totalCount,
              totalPages,
              onPageChange: (p) => setPage(p),
              onPageSizeChange: (s) => {
                setPageSize(s)
                setPage(1)
              },
              pageSizeOptions: [10, 20, 50, 100],
            }}
          />
        </Box>
      </Box>

      {/* ── Bulk assign dialog ── */}
      <Dialog open={bulkAssignOpen} onClose={() => { setBulkAssignOpen(false); setBulkAssignUser(null) }}
        PaperProps={{ sx: { borderRadius: '12px', minWidth: 380 } }}>
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, pb: 1 }}>
          Assign {selected.size} customer{selected.size > 1 ? 's' : ''} to…
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {hasMultipleOrgsSelected ? (
            <Typography fontSize="13px" color="error" sx={{ py: 1, fontWeight: 500 }}>
              ⚠️ The selected customers belong to different organizations. Bulk assignment is only allowed for customers belonging to the same organization.
            </Typography>
          ) : (
            <>
              <Autocomplete
                options={filteredUsersList}
                getOptionLabel={(u) => u.name}
                value={bulkAssignUser}
                onChange={(_e, v) => setBulkAssignUser(v)}
                noOptionsText="No eligible users"
                renderInput={(params) => (
                  <FormInput {...params} label="Select user" size="small" autoFocus />
                )}
                renderOption={(props, u) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '6px !important' }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '10px', bgcolor: '#7C3AED' }}>
                      {u.name.split(' ').slice(0, 2).map((n: string) => n[0]?.toUpperCase() ?? '').join('')}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontSize="13px" fontWeight={600}>{u.name}</Typography>
                      <Typography fontSize="11px" color="text.secondary">
                        {u.email}
                      </Typography>
                    </Box>
                    {u.lobName && (
                      <Chip
                        label={u.lobName}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '10px',
                          bgcolor: '#F3F4F6',
                          color: '#374151',
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </Box>
                )}
                isOptionEqualToValue={(a, b) => a.id === b.id}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => { setBulkAssignOpen(false); setBulkAssignUser(null) }}
            size="small" sx={{ textTransform: 'none', color: '#6B7280' }}>
            Cancel
          </Button>
          <Button
            variant="contained" size="small" disabled={hasMultipleOrgsSelected || !bulkAssignUser || bulkAssignMutation.isPending}
            onClick={() => bulkAssignMutation.mutate()}
            sx={{ textTransform: 'none', bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}>
            {bulkAssignMutation.isPending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
})

export default SalesCustomerRetentionPage
